import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ConfigQC.scss';
import DataGrid, { Column, Button as Butt, Editing, Item, KeyboardNavigation, Paging, Scrolling, Sorting, StateStoring, Toolbar, SearchPanel, Popup, Lookup, Position, RequiredRule, DataGridTypes, Selection, Export, Form, GroupPanel, Grouping, ColumnChooser } from 'devextreme-react/data-grid';
import { Item as FormItem, Label } from 'devextreme-react/form';
import Button from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react';
import { Popup as DxPopup } from 'devextreme-react/popup';
import { SelectBox } from 'devextreme-react/select-box';
import { NumberBox } from 'devextreme-react/number-box';
import { TextBox } from 'devextreme-react/text-box';
import { DateBox } from 'devextreme-react/date-box';
import { CheckBox } from 'devextreme-react/check-box';
import ScrollView from 'devextreme-react/scroll-view';
import { useApiConfigQC } from '../../api/api.configQC';
import { useApiConfiguration } from '../../api/api.configuration';
import { MaterialControlDTO, CreateMaterialControlDTO, LoteControlDTO, CreateLoteControlDTO, ConfiguracionQcDTO, CreateConfiguracionQcDTO, TipoResultadoQC, ValorEsperadoQcDTO, ReglasWestgardMode } from '../../types/types.qualityControl';
import { useAppSelector } from '../../contexts/hooks';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import { ConvertLocalDate } from '../../utils/functions';
import { newToast } from '../../components';

const TIPOS_RESULTADO: Array<{ value: TipoResultadoQC; label: string }> = [
  { value: 'CUANTITATIVO', label: 'CUANTITATIVO' },
  { value: 'CUALITATIVO', label: 'CUALITATIVO' },
  { value: 'SEMICUANTITATIVO', label: 'SEMICUANTITATIVO' },
];

const TIPOS_VALOR_ESPERADO: Array<{ value: string; label: string }> = [
  { value: 'TEXTO', label: 'TEXTO' },
  { value: 'RANGO', label: 'RANGO' },
];

const MODOS_REGLAS_WESTGARD: Array<{ value: ReglasWestgardMode; label: string; description: string }> = [
  { value: 'MANUAL', label: 'MANUAL', description: 'Aplica 4 reglas: 1-2s, 1-3s, 2-2s, R-4s' },
  { value: 'SIGMA_AUTO', label: 'SIGMA AUTO', description: 'Selección automática según nivel Sigma' },
];

// Etiquetas del tipo de resultado del analito (An_Tr_Id → ResultTypeResult).
// Se usa como filtro opcional para acotar la lista de analitos (útil sin analizador:
// separa Microbiología / Textual / Lista de lo Numérico automatizado).
const TIPO_ANALITO_LABELS: Record<number, string> = {
  1: 'Numérico', 2: 'Textual', 3: 'Fórmula', 4: 'Plantilla', 5: 'Fecha', 6: 'Tiempo',
  7: 'Imagen', 8: 'Lista', 9: 'Microbiología', 10: 'Autocompletar', 11: 'Multilínea', 12: 'Imágenes Dx',
};

const ConfigQC = () => {
  // Permisos
  const Prm_QC_R = useAppSelector((x) => x.userInfo.Permissions?.Prm_QC_R) ?? false;
  const Prm_QC_C = useAppSelector((x) => x.userInfo.Permissions?.Prm_QC_C) ?? false;
  const Prm_QC_U = useAppSelector((x) => x.userInfo.Permissions?.Prm_QC_U) ?? false;
  const Prm_QC_D = useAppSelector((x) => x.userInfo.Permissions?.Prm_QC_D) ?? false;

  // API Hooks
  const { getMaterials, getMaterialById, newMaterial, putMaterial, deleteMaterial, getAllLots, getLotsByMaterial, getLotById, newLot, putLot, deleteLot, getAllConfigurations, getConfigurationsByLot, getConfigurationsBySede, getConfigurationById, newConfiguration, putConfiguration, deleteConfiguration } = useApiConfigQC();
  const { getAnalitos, getAnalizadores, getAnalytesByAnalyzer } = useApiConfiguration();

  // Referencias a los DataGrids
  const materialsGridRef = useRef<DataGrid>(null);
  const lotsGridRef = useRef<DataGrid>(null);
  const configurationsGridRef = useRef<DataGrid>(null);

  // Estado para datos de lookups
  const [materiales, setMateriales] = useState<MaterialControlDTO[]>([]);
  const [lotes, setLotes] = useState<LoteControlDTO[]>([]);
  const [analitos, setAnalitos] = useState<any[]>([]);
  const [analizadores, setAnalizadores] = useState<any[]>([]);
  const [sedes, setSedes] = useState<any[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [analitosFiltrados, setAnalitosFiltrados] = useState<any[]>([]);

  // Filtros "Solo activos" controlables desde UI (default: traer TODOS)
  const [soloActivosMat, setSoloActivosMat] = useState(false);
  const [soloActivosLot, setSoloActivosLot] = useState(false);
  const [soloVigentesConf, setSoloVigentesConf] = useState(false);
  // Refs para leer el valor actual dentro de los CustomStore sin re-crearlos
  const soloActivosMatRef = useRef(false);
  const soloActivosLotRef = useRef(false);
  const soloVigentesConfRef = useRef(false);

  // Estado para el popup de configuración masiva
  const [showMassConfigPopup, setShowMassConfigPopup] = useState(false);
  const [massConfigLote, setMassConfigLote] = useState<number | null>(null);
  const [massConfigSede, setMassConfigSede] = useState<number | null>(null);
  const [massConfigAnalizador, setMassConfigAnalizador] = useState<number | null>(null);
  const [massConfigAnalitos, setMassConfigAnalitos] = useState<any[]>([]);
  // Filtro opcional por tipo de resultado del analito (An_Tr_Id). null = todos.
  const [massConfigFiltroTipoAnalito, setMassConfigFiltroTipoAnalito] = useState<number | null>(null);
  const massConfigGridRef = useRef<DataGrid>(null);

  // Estado para el popup custom de configuración QC
  const [showConfigPopup, setShowConfigPopup] = useState(false);
  const [configPopupMode, setConfigPopupMode] = useState<'add' | 'edit'>('add');
  const [configFormData, setConfigFormData] = useState<any>({});
  const [configValoresEsperados, setConfigValoresEsperados] = useState<ValorEsperadoQcDTO[]>([]);

  // Tipos para lookups
  const tiposMaterial = [
    { value: 'BAJO', label: 'BAJO' },
    { value: 'NORMAL', label: 'NORMAL' },
    { value: 'PATOLOGICO', label: 'PATOLÓGICO' },
  ];

  const nivelesMaterial = [
    { value: 'NIVEL 1', label: 'NIVEL 1' },
    { value: 'NIVEL 2', label: 'NIVEL 2' },
    { value: 'NIVEL 3', label: 'NIVEL 3' },
  ];

  useEffect(() => {
    // Solo cargar datos de lookup (analitos, analizadores, sedes)
    // Los DataSources de cada grid cargan sus propios datos y actualizan el estado para lookups
    loadLookupData();
  }, []);

  // Obtener permisos de sedes del usuario
  const Sedes = useAppSelector((data) => data.configuration.Data?.ComboBox)?.find((x) => x.Name === 'SysI_Sedes')?.Data;

  const loadLookupData = async () => {
    try {
      // IMPORTANTE: cargar TODOS los lotes y materiales (activos + inactivos) para que
      // las configuraciones existentes que apunten a registros desactivados puedan
      // resolver Material / Nivel / Código de Lote en las celdas y popups.
      const [analitosData, analizadoresData, allLots, materialesData] = await Promise.all([getAnalitos(), getAnalizadores(), getAllLots(false), getMaterials(false)]);

      // Preparar analitos con formato "ID - Nombre" para mejor visualización
      const analitosConDisplay = (analitosData || []).map((analito: any) => ({
        ...analito,
        An_DisplayName: `${analito.An_Id} - ${analito.An_Nombre}`,
      }));

      setAnalitos(analitosConDisplay);
      setAnalizadores(analizadoresData || []);
      setSedes(Sedes || []);

      // Cargar materiales con display name (Nombre + Nivel + Tipo) y lotes
      const materialesConDisplay = (materialesData || []).map((m: any) => ({
        ...m,
        Qcm_DisplayName: `${m.Qcm_Nombre}${m.Qcm_Nivel ? ' - ' + m.Qcm_Nivel : ''}${m.Qcm_Tipo ? ' (' + m.Qcm_Tipo + ')' : ''}`,
      }));
      setMateriales(materialesConDisplay);
      const lotsWithDisplay = (allLots || []).map((lote: any) => {
        const material = (materialesConDisplay || []).find((m: any) => m.Qcm_Id === lote.Qcl_Qcm_Id);
        // Fallback en cascada: state materiales -> campos del DTO del lote -> sólo código
        const displayName = material
          ? `${material.Qcm_Nombre}${material.Qcm_Nivel ? ' - ' + material.Qcm_Nivel : ''} - ${lote.Qcl_Codigo}`
          : lote.MaterialNombre
          ? `${lote.MaterialNombre}${lote.MaterialNivel ? ' - ' + lote.MaterialNivel : ''} - ${lote.Qcl_Codigo}`
          : lote.Qcl_Codigo;
        return { ...lote, Qcl_DisplayName: displayName };
      });
      setLotes(lotsWithDisplay);
    } catch (error) {
      console.error('Error cargando datos de lookup:', error);
      setAnalitos([]);
      setAnalizadores([]);
      setSedes([]);
    }
  };

  // ============================================
  // REFRESCO DE LOOKUPS (state usado como fuente de verdad en TODAS las pestañas)
  // Se mantiene SIEMPRE completo (activos + inactivos), independiente del filtro
  // "Solo activos" de los grids — éste sólo recorta el grid, no el lookup.
  // ============================================
  // Ref a materiales para evitar capturar state obsoleto al recalcular displayName de lotes.
  const materialesRef = useRef<MaterialControlDTO[]>([]);
  useEffect(() => { materialesRef.current = materiales; }, [materiales]);

  const refreshMaterialesLookup = useCallback(async () => {
    try {
      const all = await getMaterials(false);
      const conDisplay = (all || []).map((m: any) => ({
        ...m,
        Qcm_DisplayName: `${m.Qcm_Nombre}${m.Qcm_Nivel ? ' - ' + m.Qcm_Nivel : ''}${m.Qcm_Tipo ? ' (' + m.Qcm_Tipo + ')' : ''}`,
      }));
      setMateriales(conDisplay);
    } catch {
      // Silencioso: no rompemos la operación principal si el refresh falla
    }
  }, [getMaterials]);

  const refreshLotesLookup = useCallback(async () => {
    try {
      const all = await getAllLots(false);
      const materialesActuales = materialesRef.current;
      const lotsWithDisplay = (all || []).map((lote: any) => {
        const material = materialesActuales.find((m: any) => m.Qcm_Id === lote.Qcl_Qcm_Id);
        const displayName = material
          ? `${material.Qcm_Nombre}${material.Qcm_Nivel ? ' - ' + material.Qcm_Nivel : ''} - ${lote.Qcl_Codigo}`
          : lote.MaterialNombre
          ? `${lote.MaterialNombre}${lote.MaterialNivel ? ' - ' + lote.MaterialNivel : ''} - ${lote.Qcl_Codigo}`
          : lote.Qcl_Codigo;
        return { ...lote, Qcl_DisplayName: displayName };
      });
      setLotes(lotsWithDisplay);
    } catch {
      // Silencioso
    }
  }, [getAllLots]);

  // Refs estables para llamar las funciones desde dentro de useMemo sin re-crear los DataSource
  const refreshMaterialesLookupRef = useRef(refreshMaterialesLookup);
  const refreshLotesLookupRef = useRef(refreshLotesLookup);
  useEffect(() => { refreshMaterialesLookupRef.current = refreshMaterialesLookup; }, [refreshMaterialesLookup]);
  useEffect(() => { refreshLotesLookupRef.current = refreshLotesLookup; }, [refreshLotesLookup]);

  // ============================================
  // MATERIALES DE CONTROL - DATA SOURCE
  // ============================================
  const materialsDataSource = useMemo(() => {
    const customStore = new CustomStore({
      key: 'Qcm_Id',
      load: () => {
        // NOTA: solo devolvemos datos al grid. NO actualizamos el state `materiales` aquí
        // para no contaminar el lookup global con el filtro "Solo activos" de este grid.
        // El state `materiales` es la fuente de verdad de los lookups y se mantiene completo
        // mediante loadLookupData() (inicial) y refreshMaterialesLookup() (tras CRUD).
        return getMaterials(soloActivosMatRef.current)
          .then((res) => ({ data: res || [], totalCount: (res || []).length }))
          .catch(() => {
            throw new Error('Error al cargar materiales de control');
          });
      },
      insert: async (values) => {
        const material: Omit<CreateMaterialControlDTO, 'Qcm_Us_Id'> = {
          Qcm_Nombre: values.Qcm_Nombre,
          Qcm_Fabricante: values.Qcm_Fabricante,
          Qcm_Tipo: values.Qcm_Tipo,
          Qcm_Nivel: values.Qcm_Nivel,
          Qcm_Descripcion: values.Qcm_Descripcion,
        };
        const result = await newMaterial(material);
        if (result) {
          materialsGridRef.current?.instance.refresh();
          refreshMaterialesLookupRef.current();
          return result;
        }
        throw new Error('Error al crear material');
      },
      update: async (key, values) => {
        // Obtener el registro completo actual
        const currentData = await getMaterialById(key);
        if (!currentData) {
          throw new Error('No se pudo obtener el material actual');
        }
        // Combinar datos actuales con los nuevos valores
        const completeData = {
          ...currentData,
          ...values,
        };
        const result = await putMaterial(key, completeData);
        if (result) {
          materialsGridRef.current?.instance.refresh();
          refreshMaterialesLookupRef.current();
          refreshLotesLookupRef.current();
          return result;
        }
        throw new Error('Error al actualizar material');
      },
      remove: async (key) => {
        const result = await deleteMaterial(key);
        if (!result) {
          throw new Error('Error al eliminar material');
        }
        materialsGridRef.current?.instance.refresh();
        refreshMaterialesLookup();
      },
    });

    return new DataSource({
      store: customStore,
      reshapeOnPush: true,
    });
  }, []);

  // ============================================
  // LOTES DE CONTROL - DATA SOURCE
  // ============================================
  const lotsDataSource = useMemo(() => {
    const customStore = new CustomStore({
      key: 'Qcl_Id',
      load: async () => {
        try {
          // Sólo devolvemos datos al grid. NO actualizamos el state `lotes` aquí
          // para no contaminar el lookup global con el filtro "Solo activos".
          const allLots = await getAllLots(soloActivosLotRef.current);
          return {
            data: allLots,
            totalCount: allLots.length,
          };
        } catch {
          throw new Error('Error al cargar lotes de control');
        }
      },
      insert: async (values) => {
        const lote: Omit<CreateLoteControlDTO, 'Qcl_Us_Id'> = {
          Qcl_Qcm_Id: values.Qcl_Qcm_Id,
          Qcl_Codigo: values.Qcl_Codigo,
          Qcl_FechaRecepcion: ConvertLocalDate(values.Qcl_FechaRecepcion),
          Qcl_FechaVence: ConvertLocalDate(values.Qcl_FechaVence),
          Qcl_TempAlmacenamientoC: values.Qcl_TempAlmacenamientoC,
          Qcl_Observaciones: values.Qcl_Observaciones,
        };
        const result = await newLot(lote);
        if (result) {
          lotsGridRef.current?.instance.refresh();
          refreshLotesLookupRef.current();
          return result;
        }
        throw new Error('Error al crear lote');
      },
      update: async (key, values) => {
        // Obtener el registro completo actual
        const currentData = await getLotById(key);
        if (!currentData) {
          throw new Error('No se pudo obtener el lote actual');
        }
        // Combinar datos actuales con los nuevos valores, solo campos del DTO
        const merged = { ...currentData, ...values };
        const completeData: Partial<CreateLoteControlDTO> = {
          Qcl_Qcm_Id: merged.Qcl_Qcm_Id,
          Qcl_Codigo: merged.Qcl_Codigo,
          Qcl_FechaRecepcion: ConvertLocalDate(merged.Qcl_FechaRecepcion),
          Qcl_FechaVence: ConvertLocalDate(merged.Qcl_FechaVence),
          Qcl_TempAlmacenamientoC: merged.Qcl_TempAlmacenamientoC,
          Qcl_Observaciones: merged.Qcl_Observaciones,
          // Enviar SIEMPRE el estado: si se omite, el backend lo interpreta como
          // false (default de bool no enviado) y el lote nunca se puede reactivar.
          Qcl_Estado: merged.Qcl_Estado,
        };
        const result = await putLot(key, completeData);
        if (result) {
          lotsGridRef.current?.instance.refresh();
          refreshLotesLookupRef.current();
          return result;
        }
        throw new Error('Error al actualizar lote');
      },
      remove: async (key) => {
        const result = await deleteLot(key);
        if (!result) {
          throw new Error('Error al eliminar lote');
        }
        lotsGridRef.current?.instance.refresh();
        refreshLotesLookupRef.current();
      },
    });

    return new DataSource({
      store: customStore,
      reshapeOnPush: true,
    });
  }, []); // Sin dependencia de materiales para evitar re-creación en cascada

  // ============================================
  // CONFIGURACIONES QC - DATA SOURCE
  // ============================================
  const configurationsDataSource = useMemo(() => {
    const customStore = new CustomStore({
      key: 'Qcc_Id',
      load: async () => {
        try {
          // UNA sola llamada HTTP para obtener TODAS las configuraciones
          const allConfigs = await getAllConfigurations(soloVigentesConfRef.current);
          return {
            data: allConfigs,
            totalCount: allConfigs.length,
          };
        } catch {
          throw new Error('Error al cargar configuraciones QC');
        }
      },
      insert: async (values) => {
        const tipoResultado = values.Qcc_TipoResultado || 'CUANTITATIVO';
        const esCuant = tipoResultado === 'CUANTITATIVO';
        const teaRaw = esCuant ? values.Qcc_TEa : null;
        const tea = teaRaw != null ? Number(teaRaw) : null;
        const modoReglasRaw: ReglasWestgardMode = values.Qcc_ReglasWestgard || 'MANUAL';
        // Sigma auto solo tiene sentido si hay TEa
        const modoReglas: ReglasWestgardMode = esCuant && tea != null ? modoReglasRaw : 'MANUAL';
        const configuracion: Omit<CreateConfiguracionQcDTO, 'Qcc_Us_Id'> = {
          Qcc_Qcl_Id: values.Qcc_Qcl_Id,
          Qcc_An_Id: values.Qcc_An_Id,
          // Analizador opcional: pruebas manuales / microscopía / rápidas no pasan por equipo.
          Qcc_Az_Id: values.Qcc_Az_Id ?? null,
          Qcc_Sd_Id: values.Qcc_Sd_Id,
          Qcc_TipoResultado: tipoResultado,
          Qcc_MediaEsperada: esCuant ? values.Qcc_MediaEsperada : null,
          Qcc_DesviacionEst: esCuant ? values.Qcc_DesviacionEst : null,
          Qcc_N: values.Qcc_N ?? 0,
          Qcc_PuntosGraficacion: values.Qcc_PuntosGraficacion,
          Qcc_FechaVigencia: ConvertLocalDate(values.Qcc_FechaVigencia),
          Qcc_FechaFin: values.Qcc_FechaFin ? ConvertLocalDate(values.Qcc_FechaFin) : values.Qcc_FechaFin,
          Qcc_TEa: tea,
          Qcc_BiasEsperado: esCuant && values.Qcc_BiasEsperado != null ? Number(values.Qcc_BiasEsperado) : null,
          Qcc_Sa: esCuant && values.Qcc_Sa != null ? Number(values.Qcc_Sa) : null,
          Qcc_CVa: esCuant && values.Qcc_CVa != null ? Number(values.Qcc_CVa) : null,
          Qcc_ReglasWestgard: modoReglas,
          ValoresEsperados: !esCuant ? values.ValoresEsperados : undefined,
        };
        const result = await newConfiguration(configuracion);
        if (result) {
          configurationsGridRef.current?.instance.refresh();
          return result;
        }
        throw new Error('Error al crear configuración');
      },
      update: async (key, values) => {
        // Obtener el registro completo actual
        const currentData = await getConfigurationById(key);
        if (!currentData) {
          throw new Error('No se pudo obtener la configuración actual');
        }
        // Combinar datos actuales con los nuevos valores
        const completeData = {
          ...currentData,
          ...values,
        };
        const result = await putConfiguration(key, completeData);
        if (result) {
          configurationsGridRef.current?.instance.refresh();
          return result;
        }
        throw new Error('Error al actualizar configuración');
      },
      remove: async (key) => {
        const result = await deleteConfiguration(key);
        if (!result) {
          throw new Error('Error al eliminar configuración');
        }
        configurationsGridRef.current?.instance.refresh();
      },
    });

    return new DataSource({
      store: customStore,
      reshapeOnPush: true,
    });
  }, []); // Sin dependencia de materiales para evitar re-creación en cascada

  // ============================================
  // HANDLERS
  // ============================================
  const handleRefreshMaterials = useCallback(() => {
    materialsGridRef.current?.instance.refresh();
  }, []);

  const handleRefreshLots = useCallback(() => {
    lotsGridRef.current?.instance.refresh();
  }, []);

  const handleRefreshConfigurations = useCallback(() => {
    configurationsGridRef.current?.instance.refresh();
  }, []);

  // Handlers de los checkboxes "Solo activos" — actualizan ref + state + refrescan
  const handleToggleSoloActivosMat = useCallback((e: any) => {
    soloActivosMatRef.current = !!e.value;
    setSoloActivosMat(!!e.value);
    materialsGridRef.current?.instance.refresh();
  }, []);

  const handleToggleSoloActivosLot = useCallback((e: any) => {
    soloActivosLotRef.current = !!e.value;
    setSoloActivosLot(!!e.value);
    lotsGridRef.current?.instance.refresh();
  }, []);

  const handleToggleSoloVigentesConf = useCallback((e: any) => {
    soloVigentesConfRef.current = !!e.value;
    setSoloVigentesConf(!!e.value);
    configurationsGridRef.current?.instance.refresh();
  }, []);

  const handleTabSelectionChanged = useCallback((e: any) => {
    setSelectedTab(e.addedItems[0].id);
  }, []);

  // Referencia para almacenar los analitos filtrados (para acceso inmediato en callbacks)
  const analitosFiltradosRef = useRef<any[]>([]);

  // Handler para cargar analitos cuando se selecciona un analizador
  const loadAnalitosByAnalizador = useCallback(
    async (analizadorId: number) => {
      if (!analizadorId) {
        setAnalitosFiltrados([]);
        analitosFiltradosRef.current = [];
        return [];
      }
      try {
        const analitosData = await getAnalytesByAnalyzer(analizadorId);
        // La API devuelve objetos con { Raa_An_Id, Analitos: { An_Id, An_Nombre } }
        // Mapeamos para extraer el analito y crear el displayName
        const analitosConDisplay = (analitosData || []).map((item: any) => {
          const analito = item.Analitos || item;
          return {
            An_Id: item.Raa_An_Id || analito.An_Id,
            An_Nombre: analito.An_Nombre,
            An_DisplayName: `${item.Raa_An_Id || analito.An_Id} - ${analito.An_Nombre || ''}`,
          };
        });
        setAnalitosFiltrados(analitosConDisplay);
        analitosFiltradosRef.current = analitosConDisplay;
        return analitosConDisplay;
      } catch (error) {
        console.error('Error cargando analitos por analizador:', error);
        setAnalitosFiltrados([]);
        analitosFiltradosRef.current = [];
        return [];
      }
    },
    [getAnalytesByAnalyzer]
  );

  // Handler para el evento onEditorPreparing del grid de configuraciones
  const onConfigEditorPreparing = useCallback(
    (e: any) => {
      if (e.parentType === 'dataRow' && e.dataField === 'Qcc_An_Id') {
        // Usar la referencia para obtener los datos más actuales
        const currentAnalitos = analitosFiltradosRef.current;
        e.editorOptions.dataSource = currentAnalitos;
        e.editorOptions.disabled = currentAnalitos.length === 0;

        // Guardar referencia al editor para poder actualizarlo después
        e.editorOptions.onInitialized = (args: any) => {
          (window as any).__analitoEditorInstance = args.component;
        };
      }
      if (e.parentType === 'dataRow' && e.dataField === 'Qcc_Az_Id') {
        const defaultOnValueChanged = e.editorOptions.onValueChanged;
        e.editorOptions.onValueChanged = async (args: any) => {
          // Llamar al handler original si existe
          if (defaultOnValueChanged) {
            defaultOnValueChanged(args);
          }
          e.setValue(args.value);

          // Cargar los analitos del analizador seleccionado
          const nuevosAnalitos = await loadAnalitosByAnalizador(args.value);

          // Obtener el editor del analito y actualizar su dataSource
          const analitoEditor = (window as any).__analitoEditorInstance;
          if (analitoEditor) {
            analitoEditor.option('dataSource', nuevosAnalitos);
            analitoEditor.option('disabled', nuevosAnalitos.length === 0);
            analitoEditor.option('value', null);
          }
        };
      }
    },
    [loadAnalitosByAnalizador]
  );

  // Handler para cuando se inicia la edición de una fila de configuración
  const onConfigEditingStart = useCallback(
    async (e: any) => {
      // Si hay un analizador seleccionado, cargar sus analitos
      if (e.data?.Qcc_Az_Id) {
        await loadAnalitosByAnalizador(e.data.Qcc_Az_Id);
      } else {
        setAnalitosFiltrados([]);
        analitosFiltradosRef.current = [];
      }
    },
    [loadAnalitosByAnalizador]
  );

  // Handler para cuando se inicia una nueva fila
  const onConfigInitNewRow = useCallback((e: any) => {
    e.data.Qcc_N = 0;
    e.data.Qcc_Estado = true;
    // Limpiar los analitos filtrados al crear nuevo registro
    setAnalitosFiltrados([]);
    analitosFiltradosRef.current = [];
  }, []);

  // Handler para validar antes de eliminar una configuración
  const onConfigRowRemoving = useCallback((e: any) => {
    if (e.data.CantidadMediciones && e.data.CantidadMediciones > 0) {
      e.cancel = true;
      newToast('ERROR', `No se puede eliminar la configuración porque tiene ${e.data.CantidadMediciones} mediciones asociadas`, 4);
    }
  }, []);

  // ============================================
  // POPUP CUSTOM DE CONFIGURACIÓN QC
  // ============================================
  const handleOpenConfigPopupAdd = useCallback(() => {
    setConfigPopupMode('add');
    setConfigFormData({
      Qcc_TipoResultado: 'CUANTITATIVO',
      Qcc_N: 0,
      Qcc_PuntosGraficacion: 20,
      Qcc_Estado: true,
      Qcc_TEa: null,
      Qcc_BiasEsperado: null,
      Qcc_Sa: null,
      Qcc_CVa: null,
      Qcc_ReglasWestgard: 'MANUAL' as ReglasWestgardMode,
    });
    setConfigValoresEsperados([]);
    setAnalitosFiltrados([]);
    analitosFiltradosRef.current = [];
    setShowConfigPopup(true);
  }, []);

  const handleOpenConfigPopupEdit = useCallback(async (data: any) => {
    setConfigPopupMode('edit');
    // Cargar datos completos de la configuración
    const fullConfig = await getConfigurationById(data.Qcc_Id);
    if (!fullConfig) {
      newToast('ERROR', 'No se pudo cargar la configuración', 4);
      return;
    }
    // Conservar el contador de mediciones desde la fila del grid como respaldo
    // (por si getConfigurationById no lo devuelve): decide el mensaje de edición.
    setConfigFormData({ ...fullConfig, CantidadMediciones: fullConfig.CantidadMediciones ?? data.CantidadMediciones ?? 0 });
    setConfigValoresEsperados(fullConfig.ValoresEsperados || []);
    // Cargar analitos filtrados si hay analizador
    if (fullConfig.Qcc_Az_Id) {
      await loadAnalitosByAnalizador(fullConfig.Qcc_Az_Id);
    }
    setShowConfigPopup(true);
  }, [getConfigurationById, loadAnalitosByAnalizador]);

  const handleCloseConfigPopup = useCallback(() => {
    setShowConfigPopup(false);
    setConfigFormData({});
    setConfigValoresEsperados([]);
  }, []);

  const handleConfigFormFieldChange = useCallback((field: string, value: any) => {
    setConfigFormData((prev: any) => ({ ...prev, [field]: value }));
  }, []);

  const handleAddValorEsperado = useCallback(() => {
    setConfigValoresEsperados((prev) => [
      ...prev,
      { Qcve_TipoValor: 'TEXTO', Qcve_ValorTexto: '', Qcve_RangoMinimo: null, Qcve_RangoMaximo: null, Qcve_EsControlBueno: true, Qcve_Descripcion: '' },
    ]);
  }, []);

  const handleRemoveValorEsperado = useCallback((index: number) => {
    setConfigValoresEsperados((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleValorEsperadoChange = useCallback((index: number, field: string, value: any) => {
    setConfigValoresEsperados((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  }, []);

  const handleSaveConfigPopup = useCallback(async () => {
    const tipoResultado: TipoResultadoQC = configFormData.Qcc_TipoResultado || 'CUANTITATIVO';

    // Validaciones
    if (!configFormData.Qcc_Qcl_Id) { newToast('ERROR', 'Seleccione un Lote', 4); return; }
    if (!configFormData.Qcc_Sd_Id) { newToast('ERROR', 'Seleccione una Sede', 4); return; }
    if (!configFormData.Qcc_An_Id) { newToast('ERROR', 'Seleccione un Analito', 4); return; }
    if (!configFormData.Qcc_FechaVigencia) { newToast('ERROR', 'Ingrese la Fecha de Vigencia', 4); return; }

    if (tipoResultado === 'CUANTITATIVO') {
      if (configFormData.Qcc_MediaEsperada == null) {
        newToast('ERROR', 'Ingrese la Media Esperada', 4); return;
      }
      if (!configFormData.Qcc_DesviacionEst || configFormData.Qcc_DesviacionEst <= 0) {
        newToast('ERROR', 'La Desviación Estándar debe ser mayor a 0', 4); return;
      }
    } else {
      // CUALITATIVO o SEMICUANTITATIVO
      if (configValoresEsperados.length === 0) {
        newToast('ERROR', 'Debe agregar al menos un Valor Esperado', 4); return;
      }
      for (let i = 0; i < configValoresEsperados.length; i++) {
        const ve = configValoresEsperados[i];
        if (ve.Qcve_TipoValor === 'TEXTO') {
          if (!ve.Qcve_ValorTexto || ve.Qcve_ValorTexto.trim() === '') {
            newToast('ERROR', `Valor Esperado #${i + 1}: El texto no puede estar vacío`, 4); return;
          }
        } else if (ve.Qcve_TipoValor === 'RANGO') {
          if (ve.Qcve_RangoMinimo == null && ve.Qcve_RangoMaximo == null) {
            newToast('ERROR', `Valor Esperado #${i + 1}: Debe ingresar al menos un límite (min o max)`, 4); return;
          }
          if (ve.Qcve_RangoMinimo != null && ve.Qcve_RangoMaximo != null && ve.Qcve_RangoMinimo > ve.Qcve_RangoMaximo) {
            newToast('ERROR', `Valor Esperado #${i + 1}: El mínimo no puede ser mayor al máximo`, 4); return;
          }
        }
      }
    }

    const esCuant = tipoResultado === 'CUANTITATIVO';
    const teaVal = esCuant && configFormData.Qcc_TEa != null ? Number(configFormData.Qcc_TEa) : null;
    const biasVal = esCuant && configFormData.Qcc_BiasEsperado != null ? Number(configFormData.Qcc_BiasEsperado) : null;
    const saVal = esCuant && configFormData.Qcc_Sa != null ? Number(configFormData.Qcc_Sa) : null;
    const cvaVal = esCuant && configFormData.Qcc_CVa != null ? Number(configFormData.Qcc_CVa) : null;
    // Si no hay TEa, forzar MANUAL
    const modoReglas: ReglasWestgardMode = esCuant && teaVal != null ? (configFormData.Qcc_ReglasWestgard || 'MANUAL') : 'MANUAL';

    try {
      if (configPopupMode === 'add') {
        // Regla: un analito sólo puede tener una configuración vigente por lote
        const existingConfigs = await getConfigurationsByLot(configFormData.Qcc_Qcl_Id, true);
        const duplicate = existingConfigs.find((c) => c.Qcc_An_Id === configFormData.Qcc_An_Id);
        if (duplicate) {
          newToast('ERROR', `Ya existe una configuración vigente para este analito en este lote (ID: ${duplicate.Qcc_Id}). Finalice o desactive la configuración existente antes de crear una nueva.`, 6);
          return;
        }
        const values = {
          ...configFormData,
          Qcc_TipoResultado: tipoResultado,
          Qcc_MediaEsperada: esCuant ? configFormData.Qcc_MediaEsperada : null,
          Qcc_DesviacionEst: esCuant ? configFormData.Qcc_DesviacionEst : null,
          Qcc_TEa: teaVal,
          Qcc_BiasEsperado: biasVal,
          Qcc_Sa: saVal,
          Qcc_CVa: cvaVal,
          Qcc_ReglasWestgard: modoReglas,
          ValoresEsperados: !esCuant ? configValoresEsperados : undefined,
        };
        // Use the DataSource insert
        await configurationsDataSource.store().insert(values);
        configurationsGridRef.current?.instance.refresh();
      } else {
        // Edit mode — el usuario ahora puede modificar lote, sede, analizador y analito.
        // Validamos unicidad (lote + analito vigentes) excluyendo la configuración actual.
        const existingConfigs = await getConfigurationsByLot(configFormData.Qcc_Qcl_Id, true);
        const duplicate = existingConfigs.find(
          (c) => c.Qcc_An_Id === configFormData.Qcc_An_Id && c.Qcc_Id !== configFormData.Qcc_Id
        );
        if (duplicate) {
          newToast(
            'ERROR',
            `Ya existe una configuración vigente para este analito en este lote (ID: ${duplicate.Qcc_Id}). Finalice o desactive esa configuración antes de duplicarla.`,
            6
          );
          return;
        }

        const updateData: any = {
          Qcc_Qcl_Id: configFormData.Qcc_Qcl_Id,
          Qcc_An_Id: configFormData.Qcc_An_Id,
          Qcc_Az_Id: configFormData.Qcc_Az_Id ?? null,
          Qcc_Sd_Id: configFormData.Qcc_Sd_Id,
          Qcc_MediaEsperada: esCuant ? configFormData.Qcc_MediaEsperada : null,
          Qcc_DesviacionEst: esCuant ? configFormData.Qcc_DesviacionEst : null,
          Qcc_N: configFormData.Qcc_N,
          Qcc_PuntosGraficacion: configFormData.Qcc_PuntosGraficacion,
          Qcc_FechaVigencia: ConvertLocalDate(configFormData.Qcc_FechaVigencia),
          Qcc_FechaFin: configFormData.Qcc_FechaFin ? ConvertLocalDate(configFormData.Qcc_FechaFin) : null,
          Qcc_TEa: teaVal,
          Qcc_BiasEsperado: biasVal,
          Qcc_Sa: saVal,
          Qcc_CVa: cvaVal,
          Qcc_ReglasWestgard: modoReglas,
          Qcc_Estado: configFormData.Qcc_Estado ?? true,
          ValoresEsperados: !esCuant ? configValoresEsperados : undefined,
        };
        const result = await putConfiguration(configFormData.Qcc_Id, updateData);
        if (result) {
          configurationsGridRef.current?.instance.refresh();
        }
      }
      handleCloseConfigPopup();
    } catch (error) {
      console.error('Error guardando configuración:', error);
      newToast('ERROR', 'Error al guardar la configuración', 4);
    }
  }, [configFormData, configValoresEsperados, configPopupMode, configurationsDataSource, putConfiguration, handleCloseConfigPopup, getConfigurationsByLot]);

  // ============================================
  // CONFIGURACIÓN MASIVA DE ANALITOS
  // ============================================
  // Estado para tipo de resultado en configuración masiva
  const [massConfigTipoResultado, setMassConfigTipoResultado] = useState<TipoResultadoQC>('CUANTITATIVO');
  // Modo de reglas Westgard global para configuración masiva
  const [massConfigReglasWestgard, setMassConfigReglasWestgard] = useState<ReglasWestgardMode>('MANUAL');
  // Popup para configurar valores esperados por analito individual
  const [massVePopupVisible, setMassVePopupVisible] = useState(false);
  const [massVeAnalitoId, setMassVeAnalitoId] = useState<number | null>(null);
  const [massVeAnalitoNombre, setMassVeAnalitoNombre] = useState<string>('');
  const [massVeEditing, setMassVeEditing] = useState<ValorEsperadoQcDTO[]>([]);

  const handleOpenMassConfig = useCallback(() => {
    setMassConfigLote(null);
    setMassConfigSede(null);
    setMassConfigAnalizador(null);
    setMassConfigAnalitos([]);
    setMassConfigFiltroTipoAnalito(null);
    setMassConfigTipoResultado('CUANTITATIVO');
    setMassConfigReglasWestgard('MANUAL');
    setShowMassConfigPopup(true);
  }, []);

  const handleCloseMassConfig = useCallback(() => {
    setShowMassConfigPopup(false);
    setMassConfigLote(null);
    setMassConfigSede(null);
    setMassConfigAnalizador(null);
    setMassConfigAnalitos([]);
    setMassConfigFiltroTipoAnalito(null);
    setMassConfigTipoResultado('CUANTITATIVO');
    setMassConfigReglasWestgard('MANUAL');
  }, []);

  // Abrir popup de valores esperados para un analito específico
  const handleOpenMassVePopup = useCallback((analitoId: number, analitoNombre: string, valoresActuales: ValorEsperadoQcDTO[]) => {
    setMassVeAnalitoId(analitoId);
    setMassVeAnalitoNombre(analitoNombre);
    setMassVeEditing(valoresActuales ? [...valoresActuales.map(v => ({ ...v }))] : []);
    setMassVePopupVisible(true);
  }, []);

  const handleCloseMassVePopup = useCallback(() => {
    setMassVePopupVisible(false);
    setMassVeAnalitoId(null);
    setMassVeAnalitoNombre('');
    setMassVeEditing([]);
  }, []);

  const handleSaveMassVePopup = useCallback(() => {
    // Validar que los valores esperados tengan datos completos
    for (const ve of massVeEditing) {
      if (ve.Qcve_TipoValor === 'TEXTO' && !ve.Qcve_ValorTexto?.trim()) {
        newToast('ERROR', 'Complete el texto de todos los Valores Esperados de tipo TEXTO', 4);
        return;
      }
      if (ve.Qcve_TipoValor === 'RANGO' && (ve.Qcve_RangoMinimo == null || ve.Qcve_RangoMaximo == null)) {
        newToast('ERROR', 'Complete Mínimo y Máximo de todos los Valores Esperados de tipo RANGO', 4);
        return;
      }
    }
    // Guardar en el analito correspondiente
    setMassConfigAnalitos((prev) =>
      prev.map((a) => (a.An_Id === massVeAnalitoId ? { ...a, ValoresEsperados: [...massVeEditing] } : a))
    );
    handleCloseMassVePopup();
  }, [massVeAnalitoId, massVeEditing, handleCloseMassVePopup]);

  const handleAddMassVe = useCallback(() => {
    setMassVeEditing((prev) => [
      ...prev,
      { Qcve_TipoValor: 'TEXTO', Qcve_ValorTexto: '', Qcve_EsControlBueno: true, Qcve_Descripcion: '' },
    ]);
  }, []);

  const handleRemoveMassVe = useCallback((index: number) => {
    setMassVeEditing((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleMassVeChange = useCallback((index: number, field: string, value: any) => {
    setMassVeEditing((prev) =>
      prev.map((ve, i) => {
        if (i !== index) return ve;
        const updated = { ...ve, [field]: value };
        if (field === 'Qcve_TipoValor') {
          if (value === 'TEXTO') {
            updated.Qcve_RangoMinimo = null;
            updated.Qcve_RangoMaximo = null;
          } else {
            updated.Qcve_ValorTexto = null;
          }
        }
        return updated;
      })
    );
  }, []);

  // Copiar valores esperados de un analito a todos los demás que aún no tengan
  const handleCopyVeToAll = useCallback((sourceAnalitoId: number) => {
    const sourceAnalito = massConfigAnalitos.find((a) => a.An_Id === sourceAnalitoId);
    if (!sourceAnalito?.ValoresEsperados?.length) {
      newToast('ERROR', 'El analito origen no tiene valores esperados para copiar', 4);
      return;
    }
    setMassConfigAnalitos((prev) =>
      prev.map((a) => {
        if (a.An_Id === sourceAnalitoId) return a;
        // Solo copiar si el analito no tiene valores configurados aún
        if (!a.ValoresEsperados || a.ValoresEsperados.length === 0) {
          return { ...a, ValoresEsperados: sourceAnalito.ValoresEsperados.map((v: any) => ({ ...v })) };
        }
        return a;
      })
    );
    newToast('SUCCESS', 'Valores copiados a los analitos sin configurar', 2);
  }, [massConfigAnalitos]);

  // Función para cargar analitos filtrando los ya configurados
  const loadAnalitosForMassConfig = useCallback(
    async (analizadorId: number | null, loteId: number | null) => {
      // Al recargar la lista, limpiar el filtro por tipo para no ocultar la nueva data.
      setMassConfigFiltroTipoAnalito(null);
      // Sin analizador ni lote no hay forma de acotar la lista: evitamos cargar TODOS los analitos.
      if (!analizadorId && !loteId) {
        setMassConfigAnalitos([]);
        return;
      }
      try {
        // Con analizador: analitos del equipo. Sin analizador (manual/microscopía/rápidas): todos los analitos.
        const analitosData = analizadorId ? await getAnalytesByAnalyzer(analizadorId) : await getAnalitos();

        // Obtener configuraciones existentes para este lote (si hay lote seleccionado)
        let configsExistentes: ConfiguracionQcDTO[] = [];
        if (loteId) {
          configsExistentes = await getConfigurationsByLot(loteId, true);
        }

        // Mapear los datos para el grid con campos editables
        let analitosParaGrid = (analitosData || []).map((item: any) => {
          const analito = item.Analitos || item;
          return {
            An_Id: item.Raa_An_Id || analito.An_Id,
            An_Nombre: analito.An_Nombre || '',
            // Tipo de resultado del analito (para el filtro opcional); puede no venir en el flujo por analizador.
            An_Tr_Id: analito.An_Tr_Id ?? null,
            Qcc_MediaEsperada: null,
            Qcc_DesviacionEst: null,
            Qcc_N: 0,
            Qcc_PuntosGraficacion: 20,
            Qcc_FechaVigencia: new Date(),
            Qcc_FechaFin: null,
            Qcc_TEa: null,
            Qcc_BiasEsperado: null,
            Qcc_Sa: null,
            Qcc_CVa: null,
            ValoresEsperados: [] as ValorEsperadoQcDTO[],
          };
        });

        // Regla: un analito sólo puede tener una configuración vigente por lote.
        // Filtrar los analitos que ya están configurados para este lote (independiente del analizador/sede).
        if (loteId && configsExistentes.length > 0) {
          const analitosYaConfigurados = configsExistentes.map((c) => c.Qcc_An_Id);
          analitosParaGrid = analitosParaGrid.filter((a) => !analitosYaConfigurados.includes(a.An_Id));
        }

        setMassConfigAnalitos(analitosParaGrid);
      } catch (error) {
        console.error('Error cargando analitos:', error);
        setMassConfigAnalitos([]);
      }
    },
    [getAnalytesByAnalyzer, getAnalitos, getConfigurationsByLot]
  );

  const handleAnalizadorChange = useCallback(
    async (analizadorId: number | null) => {
      setMassConfigAnalizador(analizadorId);
      // Al limpiar el analizador se recarga la lista completa (acotada por el lote).
      await loadAnalitosForMassConfig(analizadorId, massConfigLote);
    },
    [loadAnalitosForMassConfig, massConfigLote]
  );

  // Recargar analitos cuando cambie el lote
  const handleLoteChange = useCallback(
    async (loteId: number) => {
      setMassConfigLote(loteId);
      // Recargar analitos aunque no haya analizador (flujo manual): el lote ya acota la lista.
      await loadAnalitosForMassConfig(massConfigAnalizador, loteId);
    },
    [loadAnalitosForMassConfig, massConfigAnalizador]
  );

  const handleSedeChange = useCallback(
    async (sedeId: number) => {
      setMassConfigSede(sedeId);
    },
    []
  );

  const handleSaveAnalitoConfig = useCallback(
    async (analitoData: any) => {
      // El analizador es opcional (pruebas manuales / microscopía / rápidas).
      if (!massConfigLote || !massConfigSede) {
        newToast('ERROR', 'Debe seleccionar Lote y Sede', 4);
        return;
      }
      if (massConfigTipoResultado === 'CUANTITATIVO' && (analitoData.Qcc_MediaEsperada == null || !analitoData.Qcc_DesviacionEst || analitoData.Qcc_DesviacionEst <= 0)) {
        newToast('ERROR', 'Debe ingresar Media y Desviación Estándar (DE > 0)', 4);
        return;
      }

      const analitoValores: ValorEsperadoQcDTO[] = analitoData.ValoresEsperados || [];

      if (massConfigTipoResultado !== 'CUANTITATIVO' && analitoValores.length === 0) {
        newToast('ERROR', `Configure los Valores Esperados para ${analitoData.An_Nombre} antes de guardar`, 4);
        return;
      }

      const esCuantMass = massConfigTipoResultado === 'CUANTITATIVO';
      const teaAnalito = esCuantMass && analitoData.Qcc_TEa != null ? Number(analitoData.Qcc_TEa) : null;
      const biasAnalito = esCuantMass && analitoData.Qcc_BiasEsperado != null ? Number(analitoData.Qcc_BiasEsperado) : null;
      const saAnalito = esCuantMass && analitoData.Qcc_Sa != null ? Number(analitoData.Qcc_Sa) : null;
      const cvaAnalito = esCuantMass && analitoData.Qcc_CVa != null ? Number(analitoData.Qcc_CVa) : null;
      // Si no tiene TEa, forzar MANUAL aunque el modo global sea SIGMA_AUTO
      const modoReglasAnalito: ReglasWestgardMode = esCuantMass && teaAnalito != null ? massConfigReglasWestgard : 'MANUAL';

      const configuracion: Omit<CreateConfiguracionQcDTO, 'Qcc_Us_Id'> = {
        Qcc_Qcl_Id: massConfigLote,
        Qcc_An_Id: analitoData.An_Id,
        Qcc_Az_Id: massConfigAnalizador ?? null,
        Qcc_Sd_Id: massConfigSede,
        Qcc_TipoResultado: massConfigTipoResultado,
        Qcc_MediaEsperada: esCuantMass ? analitoData.Qcc_MediaEsperada : null,
        Qcc_DesviacionEst: esCuantMass ? analitoData.Qcc_DesviacionEst : null,
        Qcc_N: analitoData.Qcc_N ?? 0,
        Qcc_PuntosGraficacion: analitoData.Qcc_PuntosGraficacion ?? 20,
        Qcc_FechaVigencia: ConvertLocalDate(analitoData.Qcc_FechaVigencia),
        Qcc_FechaFin: analitoData.Qcc_FechaFin ? ConvertLocalDate(analitoData.Qcc_FechaFin) : analitoData.Qcc_FechaFin,
        Qcc_TEa: teaAnalito,
        Qcc_BiasEsperado: biasAnalito,
        Qcc_Sa: saAnalito,
        Qcc_CVa: cvaAnalito,
        Qcc_ReglasWestgard: modoReglasAnalito,
        ValoresEsperados: !esCuantMass ? analitoValores : undefined,
      };

      try {
        // Regla: un analito sólo puede tener una configuración vigente por lote
        const existingConfigs = await getConfigurationsByLot(massConfigLote, true);
        const duplicate = existingConfigs.find((c) => c.Qcc_An_Id === analitoData.An_Id);
        if (duplicate) {
          newToast('ERROR', `Ya existe una configuración vigente para ${analitoData.An_Nombre} en este lote (ID: ${duplicate.Qcc_Id})`, 6);
          // Quitarlo del grid para que el usuario no lo intente de nuevo
          setMassConfigAnalitos((prev) => prev.filter((a) => a.An_Id !== analitoData.An_Id));
          return;
        }
        const result = await newConfiguration(configuracion);
        if (result) {
          newToast('SUCCESS', `Configuración guardada para ${analitoData.An_Nombre}`, 3);
          // Remover el analito del grid (ya está configurado)
          setMassConfigAnalitos((prev) => prev.filter((a) => a.An_Id !== analitoData.An_Id));
          // Refrescar el grid principal
          configurationsGridRef.current?.instance.refresh();
        }
      } catch (error) {
        console.error('Error guardando configuración:', error);
        newToast('ERROR', 'Error al guardar configuración', 4);
      }
    },
    [massConfigLote, massConfigSede, massConfigAnalizador, massConfigTipoResultado, massConfigReglasWestgard, newConfiguration, getConfigurationsByLot]
  );

  // ============================================
  // CELL PREPARE FUNCTIONS - Para colorear celdas según estado
  // ============================================
  const onLotCellPrepared = useCallback((e: any) => {
    if (e.rowType === 'data') {
      if (e.column.dataField === 'EstaVencido' && e.data.EstaVencido) {
        e.cellElement.style.backgroundColor = '#ffebee';
        e.cellElement.style.color = '#c62828';
        e.cellElement.style.fontWeight = 'bold';
      }
      if (e.column.dataField === 'DiasParaVencer' && e.data.DiasParaVencer <= 30) {
        e.cellElement.style.backgroundColor = '#fff3e0';
        e.cellElement.style.color = '#e65100';
      }
    }
  }, []);

  const onConfigCellPrepared = useCallback((e: any) => {
    if (e.rowType === 'data') {
      if (e.column.dataField === 'EstaVigente' && !e.data.EstaVigente) {
        e.cellElement.style.backgroundColor = '#f5f5f5';
        e.cellElement.style.color = '#9e9e9e';
      }
    }
  }, []);

  // Opciones del filtro por tipo de analito: solo los tipos presentes en la lista cargada.
  const tipoAnalitoOpciones = useMemo(() => {
    const ids = Array.from(new Set(massConfigAnalitos.map((a) => a.An_Tr_Id).filter((v) => v != null))) as number[];
    return ids
      .map((id) => ({ value: id, label: TIPO_ANALITO_LABELS[id] || `Tipo ${id}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [massConfigAnalitos]);

  // Lista de analitos que ve el grid, aplicando el filtro opcional por tipo.
  const massConfigAnalitosFiltrados = useMemo(() => {
    if (massConfigFiltroTipoAnalito == null) return massConfigAnalitos;
    return massConfigAnalitos.filter((a) => a.An_Tr_Id === massConfigFiltroTipoAnalito);
  }, [massConfigAnalitos, massConfigFiltroTipoAnalito]);

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper view-wrapper-contact-list'>
        <TabPanel deferRendering={true} selectedIndex={selectedTab} onSelectionChanged={handleTabSelectionChanged} swipeEnabled={false} animationEnabled={true} height='100%'>
          {/* ============================================ */}
          {/* TAB 1: MATERIALES DE CONTROL */}
          {/* ============================================ */}
          <TabItem title='Materiales de Control'>
            <DataGrid
              className='grid'
              dataSource={materialsDataSource}
              ref={materialsGridRef}
              width={'100%'}
              hoverStateEnabled={true}
              height='calc(100vh - 200px)'
              repaintChangesOnly={true}
              highlightChanges={true}
              rowAlternationEnabled={true}
              showColumnLines={true}
              onInitNewRow={(e) => {
                e.data.Qcm_Estado = true;
              }}>
              <SearchPanel visible={true} />
              <Scrolling columnRenderingMode='standard' mode='virtual' useNative={true} scrollByContent={true} showScrollbar='always' />
              <Sorting mode='multiple' />
              <Selection mode='single' />
              <Editing
                mode='popup'
                allowUpdating={Prm_QC_U}
                allowAdding={Prm_QC_C}
                allowDeleting={Prm_QC_D}
                selectTextOnEditStart={true}
                texts={{
                  saveRowChanges: 'Guardar',
                  cancelRowChanges: 'Cancelar',
                  deleteRow: 'Eliminar',
                  addRow: 'Agregar',
                }}>
                <Popup title='MATERIAL DE CONTROL' showTitle={true} width={700} height={500}>
                  <Position my='center' at='center' of={window} />
                </Popup>
                <Form colCount={1}>
                  <FormItem dataField='Qcm_Id' visible={false} />
                  <FormItem dataField='Qcm_Nombre' editorType='dxTextBox'>
                    <Label text='NOMBRE' />
                  </FormItem>
                  <FormItem dataField='Qcm_Fabricante' editorType='dxTextBox'>
                    <Label text='FABRICANTE' />
                  </FormItem>
                  <FormItem dataField='Qcm_Tipo' editorType='dxSelectBox'>
                    <Label text='TIPO' />
                  </FormItem>
                  <FormItem dataField='Qcm_Nivel' editorType='dxSelectBox'>
                    <Label text='NIVEL' />
                  </FormItem>
                  <FormItem dataField='Qcm_Descripcion' editorType='dxTextArea'>
                    <Label text='DESCRIPCIÓN' />
                  </FormItem>
                  <FormItem dataField='Qcm_Estado' editorType='dxCheckBox'>
                    <Label text='ESTADO' />
                  </FormItem>
                  <FormItem dataField='CantidadLotesActivos' visible={false} />
                </Form>
              </Editing>
              <Paging enabled={false} />
              <Toolbar>
                <Item location='before'>
                  <div className='grid-header'>Materiales de Control</div>
                </Item>
                <Item location='before'>
                  <CheckBox text='Solo activos' value={soloActivosMat} onValueChanged={handleToggleSoloActivosMat} hint='Mostrar solo materiales activos' />
                </Item>
                <Item name='searchPanel' />
                <Item name='addRowButton' />
                <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                  <Button id='RefreshMaterials' icon='refresh' text='Refresh' stylingMode='text' onClick={handleRefreshMaterials} />
                  <Tooltip target={'#RefreshMaterials'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                    Refrescar datos
                  </Tooltip>
                </Item>
              </Toolbar>
              <KeyboardNavigation editOnKeyPress={true} enterKeyAction='startEdit' enterKeyDirection='column' />
              <StateStoring enabled={true} type='custom' />

              <Column allowGrouping={false} allowSorting={false} caption='ID' allowEditing={false} visible={true} dataField='Qcm_Id' width={90} alignment='center' name='Qcm_Id' />
              <Column allowGrouping={false} caption='NOMBRE' visible={true} allowEditing={true} dataField='Qcm_Nombre' alignment='left' minWidth={200}>
                <RequiredRule />
              </Column>
              <Column allowGrouping={false} caption='FABRICANTE' allowEditing={true} allowSorting={false} dataField='Qcm_Fabricante' width={150} alignment='left' />
              <Column allowGrouping={false} caption='TIPO' visible={true} allowEditing={true} dataField='Qcm_Tipo' width={120} alignment='center'>
                <Lookup dataSource={tiposMaterial} valueExpr='value' displayExpr='label' />
                <RequiredRule />
              </Column>
              <Column allowGrouping={false} caption='NIVEL' visible={true} allowEditing={true} dataField='Qcm_Nivel' width={100} alignment='center'>
                <Lookup dataSource={nivelesMaterial} valueExpr='value' displayExpr='label' allowClearing={true} />
              </Column>
              <Column allowGrouping={false} caption='DESCRIPCIÓN' visible={false} allowEditing={true} dataField='Qcm_Descripcion' alignment='left' />
              <Column allowGrouping={false} allowSorting={false} caption='LOTES ACTIVOS' visible={true} showInColumnChooser={false} allowEditing={false} dataField='CantidadLotesActivos' width={120} alignment='center' />
              <Column allowGrouping={false} allowSorting={false} caption='ESTADO' allowEditing={true} dataField='Qcm_Estado' alignment='center' width={90} dataType='boolean' />

              <Column type='buttons' caption='ACCIÓN' width={100}>
                <Butt name='edit' />
                <Butt name='delete' />
              </Column>
            </DataGrid>
          </TabItem>

          {/* ============================================ */}
          {/* TAB 2: LOTES DE CONTROL */}
          {/* ============================================ */}
          <TabItem title='Lotes de Control'>
            <DataGrid
              className='grid'
              dataSource={lotsDataSource}
              ref={lotsGridRef}
              width={'100%'}
              hoverStateEnabled={true}
              height='calc(100vh - 200px)'
              repaintChangesOnly={true}
              highlightChanges={true}
              rowAlternationEnabled={true}
              showColumnLines={true}
              onCellPrepared={onLotCellPrepared}
              onInitNewRow={(e) => {
                e.data.Qcl_Estado = true;
              }}>
              <SearchPanel visible={true} />
              <Scrolling columnRenderingMode='standard' mode='virtual' useNative={true} scrollByContent={true} showScrollbar='always' />
              <Sorting mode='multiple' />
              <Selection mode='single' />
              <Editing
                mode='popup'
                allowUpdating={Prm_QC_U}
                allowAdding={Prm_QC_C}
                allowDeleting={Prm_QC_D}
                selectTextOnEditStart={true}
                texts={{
                  saveRowChanges: 'Guardar',
                  cancelRowChanges: 'Cancelar',
                  deleteRow: 'Eliminar',
                  addRow: 'Agregar',
                }}>
                <Popup title='LOTE DE CONTROL' showTitle={true} width={750} height={520}>
                  <Position my='center' at='center' of={window} />
                </Popup>
                <Form colCount={2}>
                  <FormItem dataField='Qcl_Id' visible={false} />
                  <FormItem dataField='Qcl_Qcm_Id' editorType='dxSelectBox' colSpan={2}>
                    <Label text='MATERIAL' />
                  </FormItem>
                  <FormItem dataField='Qcl_Codigo' editorType='dxTextBox' colSpan={2}>
                    <Label text='CÓDIGO LOTE' />
                  </FormItem>
                  <FormItem dataField='Qcl_FechaRecepcion' editorType='dxDateBox'>
                    <Label text='F. RECEPCIÓN' />
                  </FormItem>
                  <FormItem dataField='Qcl_FechaVence' editorType='dxDateBox'>
                    <Label text='F. VENCIMIENTO' />
                  </FormItem>
                  <FormItem dataField='Qcl_TempAlmacenamientoC' editorType='dxNumberBox' colSpan={2}>
                    <Label text='TEMP. °C' />
                  </FormItem>
                  <FormItem dataField='Qcl_Observaciones' editorType='dxTextArea' colSpan={2}>
                    <Label text='OBSERVACIONES' />
                  </FormItem>
                  <FormItem dataField='Qcl_Estado' editorType='dxCheckBox' colSpan={2}>
                    <Label text='ESTADO' />
                  </FormItem>
                  <FormItem dataField='MaterialTipo' visible={false} />
                  <FormItem dataField='DiasParaVencer' visible={false} />
                  <FormItem dataField='EstaVencido' visible={false} />
                  <FormItem dataField='CantidadConfiguraciones' visible={false} />
                </Form>
              </Editing>
              <Paging enabled={false} />
              <Toolbar>
                <Item location='before'>
                  <div className='grid-header'>Lotes de Control</div>
                </Item>
                <Item location='before'>
                  <CheckBox text='Solo activos' value={soloActivosLot} onValueChanged={handleToggleSoloActivosLot} hint='Mostrar solo lotes activos' />
                </Item>
                <Item name='searchPanel' />
                <Item name='addRowButton' />
                <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                  <Button id='RefreshLots' icon='refresh' text='Refresh' stylingMode='text' onClick={handleRefreshLots} />
                  <Tooltip target={'#RefreshLots'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                    Refrescar datos
                  </Tooltip>
                </Item>
              </Toolbar>
              <KeyboardNavigation editOnKeyPress={true} enterKeyAction='startEdit' enterKeyDirection='column' />
              <StateStoring enabled={true} type='custom' />

              <Column allowGrouping={false} allowSorting={false} caption='ID' allowEditing={false} visible={true} dataField='Qcl_Id' width={90} alignment='center' name='Qcl_Id' />
              <Column allowGrouping={false} caption='MATERIAL' visible={true} allowEditing={false} dataField='MaterialNombre' alignment='left' minWidth={200} />
              <Column allowGrouping={false} caption='MATERIAL' visible={false} allowEditing={true} dataField='Qcl_Qcm_Id' alignment='left'>
                <Lookup dataSource={materiales} valueExpr='Qcm_Id' displayExpr='Qcm_DisplayName' />
                <RequiredRule />
              </Column>
              <Column allowGrouping={false} allowSorting={false} caption='TIPO MATERIAL' visible={true} showInColumnChooser={false} allowEditing={false} dataField='MaterialTipo' width={120} alignment='center' />
              <Column allowGrouping={false} caption='CÓDIGO LOTE' visible={true} allowEditing={true} dataField='Qcl_Codigo' alignment='left' width={150}>
                <RequiredRule />
              </Column>
              <Column allowGrouping={false} caption='F. RECEPCIÓN' visible={true} allowEditing={true} dataField='Qcl_FechaRecepcion' width={130} alignment='center' dataType='date' format='dd/MM/yyyy'>
                <RequiredRule />
              </Column>
              <Column allowGrouping={false} caption='F. VENCIMIENTO' visible={true} allowEditing={true} dataField='Qcl_FechaVence' width={130} alignment='center' dataType='date' format='dd/MM/yyyy'>
                <RequiredRule />
              </Column>
              <Column
                allowGrouping={false}
                allowSorting={false}
                caption='DÍAS RESTANTES'
                visible={true}
                showInColumnChooser={false}
                allowEditing={false}
                dataField='DiasParaVencer'
                width={130}
                alignment='center'
                cellRender={(data) => {
                  if (data.value < 0) return <span style={{ color: '#c62828', fontWeight: 'bold' }}>VENCIDO</span>;
                  if (data.value <= 30) return <span style={{ color: '#e65100', fontWeight: 'bold' }}>{data.value}</span>;
                  return <span style={{ color: '#2e7d32' }}>{data.value}</span>;
                }}
              />
              <Column allowGrouping={false} allowSorting={false} caption='TEMP. °C' visible={false} allowEditing={true} dataField='Qcl_TempAlmacenamientoC' width={90} alignment='center' dataType='number' />
              <Column allowGrouping={false} caption='OBSERVACIONES' visible={false} allowEditing={true} dataField='Qcl_Observaciones' alignment='left' />
              <Column allowGrouping={false} allowSorting={false} caption='CONFIGS.' visible={true} showInColumnChooser={false} allowEditing={false} dataField='CantidadConfiguraciones' width={100} alignment='center' />
              <Column allowGrouping={false} allowSorting={false} caption='ESTADO' allowEditing={true} dataField='Qcl_Estado' alignment='center' width={90} dataType='boolean' />

              <Column type='buttons' caption='ACCIÓN' width={100}>
                <Butt name='edit' />
                <Butt name='delete' />
              </Column>
            </DataGrid>
          </TabItem>

          {/* ============================================ */}
          {/* TAB 3: CONFIGURACIONES QC */}
          {/* ============================================ */}
          <TabItem title='Configuraciones QC'>
            <DataGrid className='grid config-qc-grid' dataSource={configurationsDataSource} ref={configurationsGridRef} width={'100%'} hoverStateEnabled={true} height='calc(100vh - 200px)' repaintChangesOnly={true} highlightChanges={true} rowAlternationEnabled={true} showColumnLines={true} onCellPrepared={onConfigCellPrepared} onRowRemoving={onConfigRowRemoving}>
              <SearchPanel visible={true} />
              <ColumnChooser enabled={true} mode='select' />
              <Scrolling columnRenderingMode='standard' mode='virtual' useNative={true} scrollByContent={true} showScrollbar='always' />
              <Sorting mode='multiple' />
              <Selection mode='single' />
              <GroupPanel visible={false} />
              <Grouping autoExpandAll={true} allowCollapsing={true} />
              <Editing
                mode='row'
                allowUpdating={false}
                allowAdding={false}
                allowDeleting={Prm_QC_D}
                texts={{
                  deleteRow: 'Eliminar',
                }}
              />
              <Paging enabled={false} />
              <Toolbar>
                <Item location='before'>
                  <div className='grid-header'>Configuraciones QC</div>
                </Item>
                <Item location='before'>
                  <CheckBox text='Solo activas' value={soloVigentesConf} onValueChanged={handleToggleSoloVigentesConf} hint='Mostrar solo configuraciones vigentes' />
                </Item>
                <Item name='searchPanel' />
                {Prm_QC_C && (
                  <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                    <Button icon='add' text='Agregar' stylingMode='text' onClick={handleOpenConfigPopupAdd} />
                  </Item>
                )}
                <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                  <Button id='MassConfig' icon='bulletlist' text='Configuración Masiva' stylingMode='text' onClick={handleOpenMassConfig} />
                  <Tooltip target={'#MassConfig'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                    Configurar múltiples analitos por analizador
                  </Tooltip>
                </Item>
                <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                  <Button id='RefreshConfigs' icon='refresh' text='Refresh' stylingMode='text' onClick={handleRefreshConfigurations} />
                  <Tooltip target={'#RefreshConfigs'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                    Refrescar datos
                  </Tooltip>
                </Item>
              </Toolbar>
              <KeyboardNavigation editOnKeyPress={true} enterKeyAction='startEdit' enterKeyDirection='column' />
              <StateStoring enabled={true} type='custom' />

              <Column allowGrouping={false} allowSorting={false} caption='ID' allowEditing={false} visible={true} dataField='Qcc_Id' width={50} alignment='center' name='Qcc_Id' />
              <Column
                allowGrouping={true}
                allowSorting={false}
                caption='MATERIAL'
                visible={true}
                showInColumnChooser={false}
                allowEditing={false}
                dataField='MaterialNombre'
                minWidth={280}
                alignment='left'
                groupIndex={1}
                calculateCellValue={(rowData: any) => {
                  const nombre = rowData.MaterialNombre || '';
                  const nivel = rowData.MaterialNivel || '';
                  if (nombre) return nivel ? `${nombre} - ${nivel}` : nombre;
                  // Fallback: lookup por lote (lotes state tiene el join con material)
                  const lote: any = lotes.find((l: any) => l.Qcl_Id === rowData.Qcc_Qcl_Id);
                  if (lote && lote.MaterialNombre) {
                    const lnivel = lote.MaterialNivel || '';
                    return lnivel ? `${lote.MaterialNombre} - ${lnivel}` : lote.MaterialNombre;
                  }
                  return rowData.LoteCodigo ? `Lote ${rowData.LoteCodigo}` : '(Sin material)';
                }}
                calculateGroupValue={(rowData: any) => {
                  const nombre = rowData.MaterialNombre || '';
                  const nivel = rowData.MaterialNivel || '';
                  if (nombre) return nivel ? `${nombre} - ${nivel}` : nombre;
                  const lote: any = lotes.find((l: any) => l.Qcl_Id === rowData.Qcc_Qcl_Id);
                  if (lote && lote.MaterialNombre) {
                    const lnivel = lote.MaterialNivel || '';
                    return lnivel ? `${lote.MaterialNombre} - ${lnivel}` : lote.MaterialNombre;
                  }
                  return rowData.LoteCodigo ? `Lote ${rowData.LoteCodigo}` : '(Sin material)';
                }}
              />
              <Column
                allowGrouping={false}
                allowSorting={false}
                caption='LOTE'
                visible={true}
                showInColumnChooser={false}
                allowEditing={false}
                dataField='LoteCodigo'
                width={130}
                alignment='left'
                calculateCellValue={(rowData: any) => {
                  if (rowData.LoteCodigo) return rowData.LoteCodigo;
                  // Fallback: si el backend no trajo el código del lote (p.ej. lote inactivo),
                  // resolverlo desde el state `lotes` (lookup completo).
                  const lote: any = lotes.find((l: any) => l.Qcl_Id === rowData.Qcc_Qcl_Id);
                  return lote?.Qcl_Codigo || '';
                }}
              />
              <Column allowGrouping={false} allowSorting={false} caption='ANALITO' visible={true} showInColumnChooser={false} allowEditing={false} dataField='AnalitoNombre' minWidth={300} alignment='left' />
              <Column
                allowGrouping={true}
                allowSorting={false}
                caption='ANALIZADOR'
                visible={true}
                showInColumnChooser={false}
                allowEditing={false}
                dataField='AnalizadorNombre'
                width={150}
                alignment='left'
                groupIndex={0}
                calculateCellValue={(rowData: any) => rowData.AnalizadorNombre || 'PRUEBAS MANUALES'}
                calculateGroupValue={(rowData: any) => rowData.AnalizadorNombre || 'PRUEBAS MANUALES'}
                groupCellRender={(cellData: any) => <span>{cellData.value}</span>}
              />
              <Column allowGrouping={false} allowSorting={false} caption='SEDE' visible={false} showInColumnChooser={false} allowEditing={false} dataField='SedeNombre' width={120} alignment='left' />
              <Column
                allowGrouping={false}
                allowSorting={false}
                caption='TIPO'
                visible={true}
                showInColumnChooser={true}
                allowEditing={false}
                dataField='Qcc_TipoResultado'
                width={140}
                alignment='center'
                cellRender={(cellData: any) => {
                  const tipo = cellData.value || 'CUANTITATIVO';
                  let bgColor = '#2196F3';
                  if (tipo === 'CUALITATIVO') bgColor = '#2196F3';
                  else if (tipo === 'SEMICUANTITATIVO') bgColor = '#2196F3';
                  return (
                    <div style={{ backgroundColor: bgColor, color: 'white', padding: '3px 8px', borderRadius: '4px', textAlign: 'center', fontWeight: 'bold', fontSize: '11px' }}>
                      {tipo}
                    </div>
                  );
                }}
              />
              <Column allowGrouping={false} caption='MEDIA' visible={true} allowEditing={false} dataField='Qcc_MediaEsperada' width={100} alignment='center' dataType='number' format='#,##0.00' />
              <Column allowGrouping={false} caption='DESV. EST.' visible={true} allowEditing={false} dataField='Qcc_DesviacionEst' width={100} alignment='center' dataType='number' format='#,##0.00' />
              <Column
                allowGrouping={false}
                caption='N'
                visible={true}
                allowEditing={false}
                dataField='Qcc_N'
                width={90}
                alignment='center'
                dataType='number'
              />

              <Column allowGrouping={false} caption='PUNTOS GRAFICACIÓN' visible={false} allowEditing={false} dataField='Qcc_PuntosGraficacion' width={100} alignment='center' dataType='number' />
              <Column allowGrouping={false} caption='TEa (%)' visible={false} allowEditing={false} dataField='Qcc_TEa' width={100} alignment='center' dataType='number' format='#,##0.00' />
              <Column allowGrouping={false} caption='BIAS ESP. (%)' visible={false} allowEditing={false} dataField='Qcc_BiasEsperado' width={110} alignment='center' dataType='number' format='#,##0.00' />
              <Column allowGrouping={false} caption='Sa (%)' visible={false} allowEditing={false} dataField='Qcc_Sa' width={90} alignment='center' dataType='number' format='#,##0.00' />
              <Column allowGrouping={false} caption='CVa (%)' visible={false} allowEditing={false} dataField='Qcc_CVa' width={90} alignment='center' dataType='number' format='#,##0.00' />
              <Column
                allowGrouping={false}
                caption='REGLAS WESTGARD'
                visible={false}
                allowEditing={false}
                dataField='Qcc_ReglasWestgard'
                width={130}
                alignment='center'
                cellRender={(cellData: any) => {
                  const modo = cellData.value || 'MANUAL';
                  const bg = modo === 'SIGMA_AUTO' ? '#7E57C2' : '#607D8B';
                  return (
                    <div style={{ backgroundColor: bg, color: 'white', padding: '3px 8px', borderRadius: '4px', textAlign: 'center', fontWeight: 'bold', fontSize: '11px' }}>
                      {modo === 'SIGMA_AUTO' ? 'SIGMA AUTO' : 'MANUAL'}
                    </div>
                  );
                }}
              />
              <Column allowGrouping={false} caption='F. VIGENCIA' visible={false} allowEditing={false} dataField='Qcc_FechaVigencia' width={120} alignment='center' dataType='date' format='dd/MM/yyyy' />
              <Column allowGrouping={false} caption='F. FIN' visible={false} allowEditing={false} dataField='Qcc_FechaFin' width={120} alignment='center' dataType='date' format='dd/MM/yyyy' />
              <Column allowGrouping={false} allowSorting={false} caption='VIGENTE' visible={false} showInColumnChooser={false} allowEditing={false} dataField='EstaVigente' width={80} alignment='center' dataType='boolean' />
              <Column allowGrouping={false} allowSorting={false} caption='MEDICIONES' visible={true} showInColumnChooser={false} allowEditing={false} dataField='CantidadMediciones' width={100} alignment='center' />
              <Column allowGrouping={false} allowSorting={false} caption='ESTADO' allowEditing={false} dataField='Qcc_Estado' alignment='center' width={90} dataType='boolean' />

              <Column type='buttons' caption='ACCIÓN' width={100}>
                {Prm_QC_U && <Butt icon='edit' hint='Editar' onClick={(e: any) => handleOpenConfigPopupEdit(e.row.data)} />}
                <Butt name='delete' />
              </Column>
            </DataGrid>
          </TabItem>
        </TabPanel>

        {/* ============================================ */}
        {/* POPUP DE CONFIGURACIÓN MASIVA */}
        {/* ============================================ */}
        <DxPopup visible={showMassConfigPopup} onHiding={handleCloseMassConfig} title='CONFIGURACIÓN MASIVA DE ANALITOS' showCloseButton={true} height='90%' width={'90%'}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '10px' }}>
            {/* Selectores superiores */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1', minWidth: '180px' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>SEDE *</label>
                <SelectBox dataSource={sedes} valueExpr='Sd_Id' displayExpr='Sd_Nombre' value={massConfigSede} onValueChanged={(e) => handleSedeChange(e.value)} placeholder='Seleccionar sede...' searchEnabled={true} showClearButton={true} height={32} />
              </div>
              <div style={{ flex: '1', minWidth: '180px' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>ANALIZADOR (opcional)</label>
                <SelectBox dataSource={analizadores} valueExpr='Az_Id' displayExpr='Az_Nombre' value={massConfigAnalizador} onValueChanged={(e) => handleAnalizadorChange(e.value)} placeholder='Sin analizador (manual)...' searchEnabled={true} showClearButton={true} height={32} />
              </div>
              <div style={{ flex: '1', minWidth: '180px' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>LOTE *</label>
                <SelectBox dataSource={lotes} valueExpr='Qcl_Id' displayExpr='Qcl_DisplayName' value={massConfigLote} onValueChanged={(e) => handleLoteChange(e.value)} placeholder='Seleccionar lote...' searchEnabled={true} showClearButton={true} height={32} />
              </div>
              <div style={{ flex: '1', minWidth: '180px' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>TIPO RESULTADO</label>
                <SelectBox dataSource={TIPOS_RESULTADO} valueExpr='value' displayExpr='label' value={massConfigTipoResultado} onValueChanged={(e) => { setMassConfigTipoResultado(e.value); if (e.value === 'CUANTITATIVO') setMassConfigAnalitos((prev) => prev.map((a) => ({ ...a, ValoresEsperados: [] }))); if (e.value !== 'CUANTITATIVO') setMassConfigReglasWestgard('MANUAL'); }} height={32} />
              </div>
              {massConfigTipoResultado === 'CUANTITATIVO' && (
                <div style={{ flex: '1', minWidth: '180px' }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>REGLAS WESTGARD (GLOBAL)</label>
                  <SelectBox
                    dataSource={MODOS_REGLAS_WESTGARD}
                    valueExpr='value'
                    displayExpr='label'
                    value={massConfigReglasWestgard}
                    onValueChanged={(e) => {
                      const nuevoModo = e.value as ReglasWestgardMode;
                      if (nuevoModo === 'SIGMA_AUTO') {
                        // Si hay analitos sin TEa, advertir
                        const sinTEa = massConfigAnalitos.some((a) => a.Qcc_TEa == null);
                        if (sinTEa) {
                          newToast('INFO', 'SIGMA_AUTO solo se aplicará a analitos con TEa definido. El resto quedará en MANUAL.', 4);
                        }
                      }
                      setMassConfigReglasWestgard(nuevoModo);
                    }}
                    height={32}
                    hint='Se aplica a todos los analitos con TEa definido. Los que no lo tengan se guardarán en MANUAL.'
                  />
                </div>
              )}
            </div>

            {/* Grid de analitos */}
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {/* Filtro opcional por tipo de analito (útil para acotar la lista, sobre todo sin analizador) */}
              {massConfigAnalitos.length > 0 && tipoAnalitoOpciones.length > 1 && (
                <div style={{ marginBottom: '6px', width: '280px' }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>FILTRAR POR TIPO DE ANALITO (opcional)</label>
                  <SelectBox
                    dataSource={tipoAnalitoOpciones}
                    valueExpr='value'
                    displayExpr='label'
                    value={massConfigFiltroTipoAnalito}
                    onValueChanged={(e) => setMassConfigFiltroTipoAnalito(e.value ?? null)}
                    placeholder='Todos los tipos'
                    showClearButton={true}
                    searchEnabled={true}
                    height={32}
                  />
                </div>
              )}
              {massConfigAnalitos.length > 0 ? (
                <div style={{ flex: 1, minHeight: 0 }}>
                <DataGrid
                  ref={massConfigGridRef}
                  dataSource={massConfigAnalitosFiltrados}
                  keyExpr='An_Id'
                  height='100%'
                  showBorders={true}
                  rowAlternationEnabled={true}
                  hoverStateEnabled={true}
                  columnAutoWidth={true}
                  onRowUpdated={(e) => {
                    // Actualizar el estado cuando se edita una fila
                    setMassConfigAnalitos((prev) => prev.map((a) => (a.An_Id === e.key ? { ...a, ...e.data } : a)));
                  }}
                  onRowPrepared={(e) => {
                    // Hacer las filas más delgadas
                    if (e.rowType === 'data' || e.rowType === 'header') {
                      e.rowElement.style.height = '32px';
                    }
                  }}>
                  <SearchPanel visible={true} />
                  <Editing mode='cell' allowUpdating={true} />
                  <Scrolling mode='virtual' />
                  <Column dataField='An_Id' caption='ID' width={70} allowEditing={false} alignment='center' />
                  <Column dataField='An_Nombre' caption='ANALITO' minWidth={180} allowEditing={false} />
                  {massConfigTipoResultado === 'CUANTITATIVO' && (
                    <Column dataField='Qcc_MediaEsperada' caption='MEDIA *' width={110} dataType='number' format='#,##0.00' alignment='center'>
                      <RequiredRule />
                    </Column>
                  )}
                  {massConfigTipoResultado === 'CUANTITATIVO' && (
                    <Column dataField='Qcc_DesviacionEst' caption='DESV. EST. *' width={110} dataType='number' format='#,##0.00' alignment='center'>
                      <RequiredRule />
                    </Column>
                  )}
                  {massConfigTipoResultado === 'CUANTITATIVO' && (
                    <Column dataField='Qcc_TEa' caption='TEa (%)' width={100} dataType='number' format='#,##0.00' alignment='center' />
                  )}
                  {massConfigTipoResultado === 'CUANTITATIVO' && (
                    <Column dataField='Qcc_BiasEsperado' caption='BIAS (%)' width={100} dataType='number' format='#,##0.00' alignment='center' />
                  )}
                  {massConfigTipoResultado === 'CUANTITATIVO' && (
                    <Column dataField='Qcc_Sa' caption='Sa (%)' width={90} dataType='number' format='#,##0.00' alignment='center' />
                  )}
                  {massConfigTipoResultado === 'CUANTITATIVO' && (
                    <Column dataField='Qcc_CVa' caption='CVa (%)' width={90} dataType='number' format='#,##0.00' alignment='center' />
                  )}
                  {massConfigTipoResultado !== 'CUANTITATIVO' && (
                    <Column caption='VALORES ESP.' width={160} allowEditing={false} alignment='center'
                      cellRender={(cellData: any) => {
                        const count = (cellData.data.ValoresEsperados || []).length;
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                            <Button
                              icon='edit'
                              text={count > 0 ? `${count} valor${count > 1 ? 'es' : ''}` : 'Configurar'}
                              stylingMode={count > 0 ? 'contained' : 'outlined'}
                              type='default'
                              height={26}
                              onClick={() => handleOpenMassVePopup(cellData.data.An_Id, cellData.data.An_Nombre, cellData.data.ValoresEsperados)}
                            />
                            {count > 0 && (
                              <Button
                                icon='copy'
                                stylingMode='text'
                                type='default'
                                hint='Copiar estos valores a los analitos sin configurar'
                                height={26}
                                onClick={() => handleCopyVeToAll(cellData.data.An_Id)}
                              />
                            )}
                          </div>
                        );
                      }}
                    />
                  )}
                  <Column dataField='Qcc_N' caption='N' width={60} dataType='number' alignment='center' />
                  <Column dataField='Qcc_PuntosGraficacion' caption='PUNTOS' width={110} dataType='number' alignment='center' />
                  <Column dataField='Qcc_FechaVigencia' caption='F. VIGENCIA' width={110} dataType='date' format='dd/MM/yyyy' alignment='center' />
                  <Column dataField='Qcc_FechaFin' caption='F. FIN' width={110} dataType='date' format='dd/MM/yyyy' alignment='center' />
                  <Column type='buttons' caption='' width={50}>
                    <Butt icon='save' hint='Guardar configuración' onClick={(e: any) => handleSaveAnalitoConfig(e.row.data)} />
                  </Column>
                </DataGrid>
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#666',
                    fontSize: '16px',
                  }}>
                  {massConfigAnalizador ? 'No hay analitos configurados para este analizador' : 'Seleccione un lote para ver los analitos'}
                </div>
              )}
            </div>
          </div>
        </DxPopup>

        {/* ============================================ */}
        {/* POPUP VALORES ESPERADOS POR ANALITO (MASS) */}
        {/* ============================================ */}
        <DxPopup
          visible={massVePopupVisible}
          onHiding={handleCloseMassVePopup}
          title={`VALORES ESPERADOS — ${massVeAnalitoNombre}`}
          showCloseButton={true}
          width={700}
          height='auto'
          maxHeight='70%'>
          <ScrollView width='100%' height='100%'>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  Configure los valores esperados para <b>{massVeAnalitoNombre}</b>
                </span>
                <Button icon='add' text='Agregar Valor' stylingMode='outlined' type='default' onClick={handleAddMassVe} />
              </div>

              {massVeEditing.length === 0 && (
                <div style={{ textAlign: 'center', color: '#999', padding: '20px', fontStyle: 'italic' }}>
                  No hay valores esperados. Agregue al menos uno.
                </div>
              )}

              {massVeEditing.map((ve, index) => (
                <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', padding: '6px', backgroundColor: index % 2 === 0 ? '#f0f0f0' : '#fff', borderRadius: '3px' }}>
                  <div style={{ width: '110px' }}>
                    {index === 0 && <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>TIPO</label>}
                    <SelectBox dataSource={TIPOS_VALOR_ESPERADO} valueExpr='value' displayExpr='label' value={ve.Qcve_TipoValor} onValueChanged={(e) => handleMassVeChange(index, 'Qcve_TipoValor', e.value)} height={30} />
                  </div>
                  {ve.Qcve_TipoValor === 'TEXTO' ? (
                    <div style={{ flex: 1 }}>
                      {index === 0 && <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>VALOR TEXTO *</label>}
                      <TextBox value={ve.Qcve_ValorTexto || ''} onValueChanged={(e) => handleMassVeChange(index, 'Qcve_ValorTexto', e.value)} placeholder='Ej: Negativo, Positivo, 1+, Trazas' height={30} />
                    </div>
                  ) : (
                    <>
                      <div style={{ width: '100px' }}>
                        {index === 0 && <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>MÍNIMO *</label>}
                        <NumberBox value={ve.Qcve_RangoMinimo ?? undefined} onValueChanged={(e) => handleMassVeChange(index, 'Qcve_RangoMinimo', e.value)} format='#,##0.000' showClearButton={true} height={30} />
                      </div>
                      <div style={{ width: '100px' }}>
                        {index === 0 && <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>MÁXIMO *</label>}
                        <NumberBox value={ve.Qcve_RangoMaximo ?? undefined} onValueChanged={(e) => handleMassVeChange(index, 'Qcve_RangoMaximo', e.value)} format='#,##0.000' showClearButton={true} height={30} />
                      </div>
                    </>
                  )}
                  <div style={{ width: '120px' }}>
                    {index === 0 && <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>DESCRIPCIÓN</label>}
                    <TextBox value={ve.Qcve_Descripcion || ''} onValueChanged={(e) => handleMassVeChange(index, 'Qcve_Descripcion', e.value)} placeholder='Opcional' height={30} />
                  </div>
                  <div style={{ width: '90px', textAlign: 'center' }}>
                    {index === 0 && <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>CTRL. BUENO</label>}
                    <CheckBox value={ve.Qcve_EsControlBueno} onValueChanged={(e) => handleMassVeChange(index, 'Qcve_EsControlBueno', e.value)} />
                  </div>
                  <div>
                    <Button icon='trash' stylingMode='text' type='danger' onClick={() => handleRemoveMassVe(index)} hint='Eliminar' />
                  </div>
                </div>
              ))}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '10px', borderTop: '1px solid #ddd', paddingTop: '10px' }}>
                <Button text='Cancelar' stylingMode='outlined' onClick={handleCloseMassVePopup} />
                <Button text='Guardar Valores' type='default' stylingMode='contained' icon='save' onClick={handleSaveMassVePopup} />
              </div>
            </div>
          </ScrollView>
        </DxPopup>

        {/* ============================================ */}
        {/* POPUP CUSTOM DE CONFIGURACIÓN QC */}
        {/* ============================================ */}
        <DxPopup
          visible={showConfigPopup}
          onHiding={handleCloseConfigPopup}
          title={configPopupMode === 'add' ? 'NUEVA CONFIGURACIÓN DE CONTROL DE CALIDAD' : 'EDITAR CONFIGURACIÓN DE CONTROL DE CALIDAD'}
          showCloseButton={true}
          width={900}
          height={'auto'}
          maxHeight='90%'>
          <ScrollView width='100%' height='100%'>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '10px' }}>
              {/* Fila 1: Tipo Resultado */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>TIPO DE RESULTADO *</label>
                  <SelectBox
                    dataSource={TIPOS_RESULTADO}
                    valueExpr='value'
                    displayExpr='label'
                    value={configFormData.Qcc_TipoResultado || 'CUANTITATIVO'}
                    onValueChanged={(e) => handleConfigFormFieldChange('Qcc_TipoResultado', e.value)}
                    disabled={configPopupMode === 'edit'}
                    height={32}
                  />
                </div>
              </div>

              {/* Fila 2: Lote, Sede */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>LOTE *</label>
                  <SelectBox
                    dataSource={lotes}
                    valueExpr='Qcl_Id'
                    displayExpr='Qcl_DisplayName'
                    value={configFormData.Qcc_Qcl_Id}
                    onValueChanged={(e) => handleConfigFormFieldChange('Qcc_Qcl_Id', e.value)}
                    placeholder='Seleccionar lote...'
                    searchEnabled={true}
                    showClearButton={true}
                    disabled={configPopupMode === 'edit'}
                    height={32}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>SEDE *</label>
                  <SelectBox
                    dataSource={sedes}
                    valueExpr='Sd_Id'
                    displayExpr='Sd_Nombre'
                    value={configFormData.Qcc_Sd_Id}
                    onValueChanged={(e) => handleConfigFormFieldChange('Qcc_Sd_Id', e.value)}
                    placeholder='Seleccionar sede...'
                    searchEnabled={true}
                    showClearButton={true}
                    disabled={configPopupMode === 'edit'}
                    height={32}
                  />
                </div>
              </div>

              {/* Fila 3: Analizador, Analito */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>ANALIZADOR (opcional)</label>
                  <SelectBox
                    dataSource={analizadores}
                    valueExpr='Az_Id'
                    displayExpr='Az_Nombre'
                    value={configFormData.Qcc_Az_Id}
                    onValueChanged={async (e) => {
                      const prev = configFormData.Qcc_Az_Id;
                      handleConfigFormFieldChange('Qcc_Az_Id', e.value);
                      if (e.value) {
                        await loadAnalitosByAnalizador(e.value);
                      } else {
                        setAnalitosFiltrados([]);
                        analitosFiltradosRef.current = [];
                      }
                      // Limpiar analito si el analizador realmente cambió (no en la primera
                      // sincronización del popup en edición, donde prev === undefined).
                      if (prev != null && prev !== e.value) {
                        handleConfigFormFieldChange('Qcc_An_Id', null);
                      } else if (configPopupMode === 'add') {
                        handleConfigFormFieldChange('Qcc_An_Id', null);
                      }
                    }}
                    placeholder='Seleccionar analizador...'
                    searchEnabled={true}
                    showClearButton={true}
                    disabled={configPopupMode === 'edit'}
                    height={32}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>ANALITO *</label>
                  <SelectBox
                    dataSource={analitosFiltrados.length > 0 ? analitosFiltrados : analitos}
                    valueExpr='An_Id'
                    displayExpr='An_DisplayName'
                    value={configFormData.Qcc_An_Id}
                    onValueChanged={(e) => handleConfigFormFieldChange('Qcc_An_Id', e.value)}
                    placeholder='Seleccionar analito...'
                    searchEnabled={true}
                    showClearButton={true}
                    disabled={configPopupMode === 'edit'}
                    height={32}
                  />
                </div>
              </div>

              {/* Nota: la identidad (lote + analito + sede + analizador) es inmutable en edición.
                  El mensaje se adapta según si la configuración ya tiene mediciones registradas. */}
              {configPopupMode === 'edit' && (
                <div style={{ fontSize: '11px', color: '#e65100', background: '#fff3e0', borderRadius: '4px', padding: '6px 10px', margin: '2px 0' }}>
                  {(configFormData.CantidadMediciones ?? 0) > 0
                    ? 'El tipo de resultado, lote, analito, sede y analizador no se pueden modificar aquí. Para cambiarlos, desactive o finalice esta configuración y cree una nueva.'
                    : 'El tipo de resultado, lote, analito, sede y analizador no se pueden modificar aquí. Como esta configuración no tiene mediciones, puede eliminarla y crearla de nuevo con los datos correctos.'}
                </div>
              )}

              {/* Fila 4: Media y Desviación Estándar (solo CUANTITATIVO) */}
              {(configFormData.Qcc_TipoResultado || 'CUANTITATIVO') === 'CUANTITATIVO' && (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>MEDIA ESPERADA *</label>
                    <NumberBox value={configFormData.Qcc_MediaEsperada} onValueChanged={(e) => handleConfigFormFieldChange('Qcc_MediaEsperada', e.value)} format='#,##0.00' height={32} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>DESVIACIÓN ESTÁNDAR *</label>
                    <NumberBox value={configFormData.Qcc_DesviacionEst} onValueChanged={(e) => handleConfigFormFieldChange('Qcc_DesviacionEst', e.value)} format='#,##0.00' height={32} />
                  </div>
                </div>
              )}

              {/* Fila Métricas Sigma (solo CUANTITATIVO) */}
              {(configFormData.Qcc_TipoResultado || 'CUANTITATIVO') === 'CUANTITATIVO' && (
                <div style={{ border: '1px solid #e0e0e0', borderRadius: '4px', padding: '10px', background: '#fafbfc' }}>
                  <label style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '8px', display: 'block', color: '#1976d2' }}>MÉTRICAS SIGMA</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>TEa (%) — Error Total Admisible</label>
                      <NumberBox
                        value={configFormData.Qcc_TEa ?? undefined}
                        onValueChanged={(e) => handleConfigFormFieldChange('Qcc_TEa', e.value)}
                        valueChangeEvent='keyup change blur'
                        format='#,##0.00'
                        showClearButton={true}
                        height={32}
                        placeholder='Ej: 10.0'
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>BIAS ESPERADO (%)</label>
                      <NumberBox
                        value={configFormData.Qcc_BiasEsperado ?? undefined}
                        onValueChanged={(e) => handleConfigFormFieldChange('Qcc_BiasEsperado', e.value)}
                        valueChangeEvent='keyup change blur'
                        format='#,##0.00'
                        showClearButton={true}
                        height={32}
                        placeholder='Ej: 2.5'
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>REGLAS WESTGARD</label>
                      <SelectBox
                        dataSource={MODOS_REGLAS_WESTGARD}
                        valueExpr='value'
                        displayExpr='label'
                        value={configFormData.Qcc_ReglasWestgard || 'MANUAL'}
                        onValueChanged={(e) => handleConfigFormFieldChange('Qcc_ReglasWestgard', e.value as ReglasWestgardMode)}
                        height={32}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }} title='Sesgo Admisible (%) — OPCIONAL. Sesgo deseable tomado de la tabla de variabilidad biológica del analito (Ricós/EFLM o CLIA). No interviene en el cálculo Sigma; solo alimenta el Índice de Sesgo (IS = |Sesgo real| / Sa) de los informes.'>Sa (%) — Sesgo Admisible (opcional)</label>
                      <NumberBox
                        value={configFormData.Qcc_Sa ?? undefined}
                        onValueChanged={(e) => handleConfigFormFieldChange('Qcc_Sa', e.value)}
                        valueChangeEvent='keyup change blur'
                        format='#,##0.00'
                        showClearButton={true}
                        height={32}
                        placeholder='Ej: 9.00'
                        hint='Sesgo Admisible (%) — OPCIONAL. Sesgo deseable de la tabla de variabilidad biológica del analito (Ricós/EFLM o CLIA). No interviene en el cálculo Sigma; solo alimenta el Índice de Sesgo (IS) de los informes.'
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }} title='Coeficiente de Variación Admisible (%). Proviene de tablas de variabilidad biológica (Ricos/CLIA).'>CVa (%) — CV Admisible</label>
                      <NumberBox
                        value={configFormData.Qcc_CVa ?? undefined}
                        onValueChanged={(e) => handleConfigFormFieldChange('Qcc_CVa', e.value)}
                        valueChangeEvent='keyup change blur'
                        format='#,##0.00'
                        showClearButton={true}
                        height={32}
                        placeholder='Ej: 6.70'
                        hint='Coeficiente de Variación Admisible (%). Proviene de tablas de variabilidad biológica (Ricos/CLIA).'
                      />
                    </div>
                    <div style={{ flex: 1 }} />
                  </div>
                  {configFormData.Qcc_ReglasWestgard === 'SIGMA_AUTO' && (configFormData.Qcc_TEa == null) ? (
                    <div style={{ fontSize: '11px', color: '#c62828', marginTop: '6px', fontWeight: 600 }}>
                      ⚠ SIGMA_AUTO requiere TEa. Sin TEa se guardará en MANUAL automáticamente.
                    </div>
                  ) : (
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '6px', fontStyle: 'italic' }}>
                      {configFormData.Qcc_ReglasWestgard === 'SIGMA_AUTO'
                        ? 'SIGMA_AUTO: el sistema selecciona reglas automáticamente según el nivel Sigma calculado.'
                        : 'MANUAL: se aplican las 4 reglas clásicas (1-2s, 1-3s, 2-2s, R-4s).'}
                    </div>
                  )}
                </div>
              )}

              {/* Fila 5: N, Puntos, Fechas */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>N (MEDICIONES)</label>
                  <NumberBox value={configFormData.Qcc_N ?? 0} onValueChanged={(e) => handleConfigFormFieldChange('Qcc_N', e.value)} height={32} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>PUNTOS GRAFICACIÓN</label>
                  <NumberBox value={configFormData.Qcc_PuntosGraficacion ?? 20} onValueChanged={(e) => handleConfigFormFieldChange('Qcc_PuntosGraficacion', e.value)} height={32} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>F. VIGENCIA *</label>
                  <DateBox value={configFormData.Qcc_FechaVigencia} onValueChanged={(e) => handleConfigFormFieldChange('Qcc_FechaVigencia', e.value)} displayFormat='dd/MM/yyyy' height={32} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>F. FIN</label>
                  <DateBox value={configFormData.Qcc_FechaFin} onValueChanged={(e) => handleConfigFormFieldChange('Qcc_FechaFin', e.value)} displayFormat='dd/MM/yyyy' showClearButton={true} height={32} />
                </div>
              </div>

              {/* Estado activo/inactivo (solo en modo edición) */}
              {configPopupMode === 'edit' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 10px', borderRadius: '4px', border: '1px solid #e0e0e0', background: '#fafbfc' }}>
                  <CheckBox
                    value={configFormData.Qcc_Estado ?? true}
                    onValueChanged={(e) => handleConfigFormFieldChange('Qcc_Estado', e.value)}
                    text='Configuración activa'
                  />
                </div>
              )}

              {/* Sección de Valores Esperados (solo CUALITATIVO y SEMICUANTITATIVO) */}
              {(configFormData.Qcc_TipoResultado === 'CUALITATIVO' || configFormData.Qcc_TipoResultado === 'SEMICUANTITATIVO') && (
                <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ fontWeight: 'bold', fontSize: '13px' }}>VALORES ESPERADOS *</label>
                    <Button icon='add' text='Agregar Valor' stylingMode='outlined' type='default' onClick={handleAddValorEsperado} />
                  </div>
                  {configValoresEsperados.length === 0 && (
                    <div style={{ textAlign: 'center', color: '#999', padding: '15px', fontStyle: 'italic' }}>
                      No hay valores esperados. Agregue al menos uno.
                    </div>
                  )}
                  {configValoresEsperados.map((ve, index) => (
                    <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '6px', padding: '6px', backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff', borderRadius: '3px' }}>
                      <div style={{ width: '110px' }}>
                        {index === 0 && <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>TIPO</label>}
                        <SelectBox
                          dataSource={TIPOS_VALOR_ESPERADO}
                          valueExpr='value'
                          displayExpr='label'
                          value={ve.Qcve_TipoValor}
                          onValueChanged={(e) => handleValorEsperadoChange(index, 'Qcve_TipoValor', e.value)}
                          height={30}
                        />
                      </div>
                      {ve.Qcve_TipoValor === 'TEXTO' ? (
                        <div style={{ flex: 1 }}>
                          {index === 0 && <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>VALOR TEXTO *</label>}
                          <TextBox value={ve.Qcve_ValorTexto || ''} onValueChanged={(e) => handleValorEsperadoChange(index, 'Qcve_ValorTexto', e.value)} placeholder='Ej: Negativo, 1+, Trazas' height={30} />
                        </div>
                      ) : (
                        <>
                          <div style={{ width: '100px' }}>
                            {index === 0 && <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>MÍNIMO</label>}
                            <NumberBox value={ve.Qcve_RangoMinimo ?? undefined} onValueChanged={(e) => handleValorEsperadoChange(index, 'Qcve_RangoMinimo', e.value)} format='#,##0.000' showClearButton={true} height={30} />
                          </div>
                          <div style={{ width: '100px' }}>
                            {index === 0 && <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>MÁXIMO</label>}
                            <NumberBox value={ve.Qcve_RangoMaximo ?? undefined} onValueChanged={(e) => handleValorEsperadoChange(index, 'Qcve_RangoMaximo', e.value)} format='#,##0.000' showClearButton={true} height={30} />
                          </div>
                        </>
                      )}
                      <div style={{ width: '120px' }}>
                        {index === 0 && <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>DESCRIPCIÓN</label>}
                        <TextBox value={ve.Qcve_Descripcion || ''} onValueChanged={(e) => handleValorEsperadoChange(index, 'Qcve_Descripcion', e.value)} placeholder='Opcional' height={30} />
                      </div>
                      <div style={{ width: '90px', textAlign: 'center' }}>
                        {index === 0 && <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>CTRL. BUENO</label>}
                        <CheckBox value={ve.Qcve_EsControlBueno} onValueChanged={(e) => handleValorEsperadoChange(index, 'Qcve_EsControlBueno', e.value)} />
                      </div>
                      <div>
                        <Button icon='trash' stylingMode='text' type='danger' onClick={() => handleRemoveValorEsperado(index)} hint='Eliminar' />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Valores esperados de lectura (en modo edición, mostrar los existentes de la config) */}
              {configPopupMode === 'edit' && configFormData.Qcc_TipoResultado === 'CUANTITATIVO' && configFormData.ValoresEsperados && configFormData.ValoresEsperados.length > 0 && (
                <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '10px' }}>
                  <label style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '8px', display: 'block' }}>VALORES ESPERADOS (solo lectura)</label>
                  {configFormData.ValoresEsperados.map((ve: ValorEsperadoQcDTO, index: number) => (
                    <div key={index} style={{ padding: '4px 8px', backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff' }}>
                      {ve.Qcve_TipoValor === 'TEXTO' ? ve.Qcve_ValorTexto : `Rango: ${ve.Qcve_RangoMinimo ?? '-'} a ${ve.Qcve_RangoMaximo ?? '-'}`}
                      {' - '}
                      {ve.Qcve_EsControlBueno ? 'Control Bueno' : 'Control Malo'}
                      {ve.Qcve_Descripcion ? ` (${ve.Qcve_Descripcion})` : ''}
                    </div>
                  ))}
                </div>
              )}

              {/* Botones de acción */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '5px' }}>
                <Button text='Cancelar' stylingMode='outlined' onClick={handleCloseConfigPopup} />
                <Button text='Guardar' type='default' stylingMode='contained' onClick={handleSaveConfigPopup} />
              </div>
            </div>
          </ScrollView>
        </DxPopup>
      </div>
    </div>
  );
};

export default ConfigQC;
